import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { HINT_MAX, NARRATIVE_KIND } from '@platform/schema/content';
import { SUBHEADLINE_WORDS_MAX } from '@platform/schema/publish-rules';
import { Fragment, useState } from 'react';

import {
  answerFields,
  answerUnit,
  bucketLabels,
  keyedAmounts,
} from './answers';
import { CastInspector } from './CastInspector';
import { EditableNumber } from './EditableNumber';
import { EditableText } from './EditableText';
import {
  editableItems,
  sceneBody,
  sceneHint,
  sceneKindName,
  sceneVisualOf,
} from './edits';
import { FieldWarnings } from './FieldWarnings';
import { fixedCounts, stillFixed } from './fixes';
import { MechanicPicker } from './MechanicPicker';
import { Regenerate } from './Regenerate';
import { rewriteChoices } from './regeneration';
import { figureOf } from './scene-cast';
import { SceneImage } from './SceneImage';
import { needsSource } from './source';
import { sharedWarning } from './usage';

import type { ReviewScene, ScenePatch } from './edits';
import type { RewriteChoice } from './regeneration';
import type { FixSceneFieldResponse } from '@platform/client';
import type { AuthorableKind, LessonCast } from '@platform/schema/content';
import type { FixField } from '@platform/schema/scene-fix';
import type { ReactNode } from 'react';

/**
 * One scene's text, open for correction — spec 013 §12.
 *
 * An item is saved by id and only its label moves, so an edit here cannot re-key an
 * answer. The explanation is written to the `Question`, which several lessons may share:
 * §12.1's copy-on-write is for regeneration, and fixing a wrong sentence is a fix
 * everywhere it appears rather than a fork.
 */
export function SceneEditor({
  scene,
  cast = [],
  usedIn,
  onScene,
  onQuestion,
  onRegenerate,
  onSource,
  onMechanic,
  onFix,
  onRevert,
  onJump,
  onOpen,
  onImage,
  jumpRefused,
  remove,
  fixVisual,
  disabled,
}: {
  scene: ReviewScene;
  /** The lesson's cast: who the scene may seat. */
  cast?: LessonCast | undefined;
  usedIn: number;
  /** The control that takes this scene out of the lesson — GEN-45. */
  remove?: ReactNode;
  /** Fix with AI on the scene's characters, shown once it seats some — GEN-135. */
  fixVisual?: ReactNode;
  /** Open the preview on this scene — GEN-90. */
  onJump: () => void;
  /** The scene's form was opened, which makes it the one being discussed. */
  onOpen?: () => void;
  /** The scene's image changed, which the lesson has to be re-read to show. */
  onImage: () => void;
  /** Why it cannot be, shown where the control is, or nothing. */
  jumpRefused?: string | undefined;
  onScene: (patch: ScenePatch) => void;
  onQuestion: (questionId: string, explanation: string) => void;
  onRegenerate: (choice: RewriteChoice, instruction: string) => void;
  /** Records where the claim came from. Only asked for where the gate asks. */
  onSource: (input: string) => void;
  /** Changes how this question is asked, keeping what it asks — GEN-78. */
  onMechanic: (kind: AuthorableKind) => void;
  /** Fix with AI: rewrites and saves the field. Rejects when nothing was saved. */
  onFix: (field: FixField) => Promise<FixSceneFieldResponse>;
  /** Puts back the text a fix replaced. */
  onRevert: (fix: FixSceneFieldResponse) => Promise<void>;
  disabled: boolean;
}) {
  const items = editableItems(scene);
  const answers = answerFields(scene);
  const amounts = keyedAmounts(scene);
  const buckets = bucketLabels(scene);
  const unit = answerUnit(scene);
  const body = sceneBody(scene);
  const sourceId = 'sourceId' in scene ? scene.sourceId : undefined;
  const ungrounded = needsSource(scene.kind, sourceId);
  const questionId = 'questionId' in scene ? scene.questionId : undefined;

  const explanation = 'explanation' in scene ? scene.explanation : undefined;
  const visual = sceneVisualOf(scene);
  const figure = figureOf(visual);
  // A chart is not a figure, so the inspector leaves it alone.
  const seats =
    figure !== undefined || (visual === undefined && cast.length > 0);

  // The last fix per field, for Revert. Held here, so leaving the page forgets it.
  const [fixes, setFixes] = useState<
    Partial<Record<FixField, FixSceneFieldResponse>>
  >({});
  const [fixing, setFixing] = useState<FixField>();
  const [failed, setFailed] = useState<FixField>();

  const warningsOn = (field: FixField) => {
    const fix = fixes[field];
    const revertable = fix !== undefined && stillFixed(scene, fix);

    return (
      <FieldWarnings
        field={field}
        warnings={scene.warnings.filter((one) => one.field === field)}
        predatesRules={field === 'body' && scene.bodyPredatesRules}
        fixed={revertable ? fixedCounts(scene, field) : undefined}
        fixing={fixing === field}
        failed={failed === field}
        disabled={disabled || (fixing !== undefined && fixing !== field)}
        onFix={() => {
          setFixing(field);
          setFailed(undefined);
          onFix(field)
            .then((applied) => {
              setFixes((previous) => ({ ...previous, [field]: applied }));
            })
            .catch(() => {
              setFailed(field);
            })
            .finally(() => {
              setFixing(undefined);
            });
        }}
        onRevert={() => {
          if (fix === undefined) return;
          void onRevert(fix).then(() => {
            setFixes(({ [field]: _reverted, ...rest }) => rest);
          });
        }}
      />
    );
  };

  return (
    // The jump sits beside the accordion rather than inside its summary, which is a
    // `<button>`: nesting one in another is invalid, and it lent the summary an
    // accessible name ending "Go to scene" that read as the row's own label. Beside it,
    // the reviewer still reaches scene 6 from the list without opening the form first —
    // which is the click the whole control exists to remove.
    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Accordion
        disableGutters
        sx={{ flex: 1 }}
        onChange={(_event, expanded) => {
          if (expanded) onOpen?.();
        }}
      >
        <AccordionSummary>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flex: 1 }}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {scene.position}. {scene.prompt}
            </Typography>
            <Chip size="small" label={sceneKindName(scene.kind)} />
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <Stack spacing={2}>
            {ungrounded && (
              <>
                <Alert severity="warning">
                  A {sceneKindName(scene.kind)} scene states something as fact,
                  so it cannot publish until it names where that came from.
                </Alert>
                <EditableText
                  label="Source — a URL, or a citation"
                  value=""
                  disabled={disabled}
                  onSave={onSource}
                />
              </>
            )}

            <EditableText
              label="Headline"
              value={scene.prompt}
              multiline
              disabled={disabled || fixing === 'prompt'}
              onSave={(prompt) => {
                onScene({ prompt });
              }}
            />
            {warningsOn('prompt')}

            {/* Under the headline rather than beside the chip in the summary: reading the
              question is what tells a reviewer whether the mechanic is the wrong one, so
              the control belongs after it and not before. `MechanicPicker` renders nothing
              for a narrative beat, which has no mechanic to change. */}
            <MechanicPicker
              kind={scene.kind}
              itemCount={items.length}
              disabled={disabled}
              onSwitch={onMechanic}
            />

            {/* Every kind may carry one, under the headline. Switching it off keeps the
              words and takes them off the learner's screen. */}
            <FormControlLabel
              label="Add subheadline"
              control={
                <Switch
                  checked={scene.bodyEnabled}
                  disabled={disabled}
                  onChange={(_event, bodyEnabled) => {
                    onScene({ bodyEnabled });
                  }}
                />
              }
            />
            {scene.bodyEnabled && (
              <>
                <EditableText
                  label={`Subheadline — under the headline. Up to two short paragraphs and ${String(SUBHEADLINE_WORDS_MAX)} words, **bold** sparingly`}
                  value={body}
                  multiline
                  disabled={disabled || fixing === 'body'}
                  onSave={(next) => {
                    onScene({ body: next });
                  }}
                />
                {warningsOn('body')}
              </>
            )}

            {/* Every kind may carry one: it clarifies the scene for a learner who is
              stuck, and is not the scene's own text. Switching it off keeps the words. */}
            <FormControlLabel
              label="Add hint"
              control={
                <Switch
                  checked={scene.hintEnabled}
                  disabled={disabled}
                  onChange={(_event, hintEnabled) => {
                    onScene({ hintEnabled });
                  }}
                />
              }
            />
            {scene.hintEnabled && (
              <>
                <EditableText
                  label={`Hint — opened by the lightbulb beside the question. Short paragraphs, **bold** sparingly, up to ${String(HINT_MAX)} characters`}
                  value={sceneHint(scene)}
                  multiline
                  disabled={disabled || fixing === 'hint'}
                  onSave={(hint) => {
                    onScene({ hint });
                  }}
                />
                {warningsOn('hint')}
              </>
            )}

            {items.map((item, index) => {
              const bucket = buckets.get(item.id);

              return (
                <Stack
                  key={item.id}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center' }}
                >
                  <EditableText
                    label={`Item ${String(index + 1)}`}
                    value={item.label}
                    disabled={disabled || fixing === 'items'}
                    onSave={(label) => {
                      onScene({ items: [{ id: item.id, label }] });
                    }}
                  />
                  {bucket !== undefined && <Chip size="small" label={bucket} />}
                </Stack>
              );
            })}
            {warningsOn('items')}

            {/* The answer, for the mechanics whose answer is not a word. */}
            {answers.map((answer) => (
              <EditableNumber
                key={answer.key}
                label={answer.label}
                value={answer.value}
                suffix={unit}
                disabled={disabled}
                onSave={(next) => {
                  onScene({ [answer.key]: next });
                }}
              />
            ))}

            {amounts.map((amount) => (
              <Fragment key={amount.id}>
                <EditableNumber
                  label={`${amount.label} — example amount`}
                  value={amount.amount}
                  suffix={unit}
                  disabled={disabled}
                  onSave={(next) => {
                    onScene({ keys: [{ id: amount.id, correctAmount: next }] });
                  }}
                />
                <EditableNumber
                  label={`${amount.label} — plus or minus`}
                  value={amount.tolerance}
                  suffix={unit}
                  disabled={disabled}
                  onSave={(next) => {
                    onScene({
                      keys: [{ id: amount.id, amountTolerance: next }],
                    });
                  }}
                />
              </Fragment>
            ))}

            {questionId !== undefined && explanation !== undefined && (
              <>
                <EditableText
                  label="Explanation"
                  value={explanation}
                  multiline
                  disabled={disabled || fixing === 'explanation'}
                  onSave={(next) => {
                    onQuestion(questionId, next);
                  }}
                />
                {warningsOn('explanation')}
              </>
            )}

            {seats && (
              <CastInspector
                // A save re-reads the lesson; a new visual starts a new draft.
                key={JSON.stringify(visual)}
                saved={figure}
                cast={cast}
                headline={scene.prompt}
                warnings={scene.warnings.filter(
                  (one) => one.field === 'visual',
                )}
                disabled={disabled}
                onSave={(next) => {
                  onScene({ visual: next });
                }}
              />
            )}
            {figure !== undefined && fixVisual}

            {Object.hasOwn(NARRATIVE_KIND, scene.kind) && (
              <SceneImage
                sceneId={scene.id}
                imageUrl={'imageUrl' in scene ? scene.imageUrl : undefined}
                hasFigure={'visual' in scene}
                disabled={disabled}
                onChanged={onImage}
              />
            )}

            <Regenerate
              choices={rewriteChoices(scene)}
              warning={sharedWarning(usedIn)}
              disabled={disabled}
              onRegenerate={onRegenerate}
            />

            {remove}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Wrapped for the reason `SceneArc` gives — a disabled button fires no events and
          would show no tooltip, which is the state the reason most needs saying. */}
      <Tooltip title={jumpRefused ?? ''}>
        <span>
          <Button
            size="small"
            disabled={disabled || jumpRefused !== undefined}
            onClick={onJump}
          >
            Go to scene
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}
